import React from "react";
import { ArrowRight } from "lucide-react";
import { AppHeader } from "../../../../components/layout/AppHeader";
import { ButtonLink } from "../../../../components/ui/button";
import { LandingFooter } from "../landing/LandingFooter";
import { FadeUp } from "../landing/FadeUp";
import {
  LANDING_SECTION_DEFINITIONS,
  renderLandingSectionTrailingCta,
  type LandingSectionSlug,
} from "./landingSectionContent";
import { useLandingScrollToSection } from "./useLandingScrollToSection";
import type { RouteMap, TradeContent, UserDisplay } from "../../types";

type LandingSectionPageProps = {
  section: LandingSectionSlug;
  loggedIn: boolean;
  hasBusiness: boolean;
  routes: RouteMap;
  csrfToken: string;
  page: string;
  userDisplay?: UserDisplay | null;
  trades?: TradeContent[];
};

const LandingSectionHero: React.FC<{
  section: LandingSectionSlug;
  routes: RouteMap;
}> = ({ section, routes }) => {
  const definition = LANDING_SECTION_DEFINITIONS[section];
  const KickerIcon = definition.kickerIcon;

  return (
    <section className="bg-background pt-20 pb-12 sm:pt-28">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <FadeUp>
          <span className="inline-flex items-center gap-2 rounded-full border border-premium-500/40 bg-premium-50 px-3 py-1 text-sm font-semibold text-premium-700">
            <KickerIcon className="h-4 w-4" aria-hidden />
            {definition.kicker}
          </span>
          <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
            {definition.heroH1}
          </h1>
          <p className="mt-6 text-lg leading-relaxed text-muted-foreground sm:text-xl">
            {definition.heroSubhead}
          </p>
          <div className="mt-8 flex flex-col gap-4 sm:flex-row">
            <ButtonLink
              to={definition.primaryCtaHref(routes)}
              size="lg"
              variant="premium"
              className="px-8 py-4 text-lg group"
            >
              {definition.primaryCtaLabel}
              <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
            </ButtonLink>
          </div>
        </FadeUp>
      </div>
    </section>
  );
};

/**
 * Internal cross-page navigation strip. Each landing-section page links to
 * every other one so search engines (and humans) can discover the rest of
 * the marketing surface from a single click.
 */
const LandingSectionExploreMore: React.FC<{
  current: LandingSectionSlug;
}> = ({ current }) => {
  const others = (
    Object.values(LANDING_SECTION_DEFINITIONS) as Array<
      (typeof LANDING_SECTION_DEFINITIONS)[LandingSectionSlug]
    >
  ).filter((definition) => definition.slug !== current);

  return (
    <section className="bg-card py-16 border-t border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <FadeUp>
          <h2 className="font-display text-2xl font-bold text-foreground sm:text-3xl">
            Explore more from ToolKit
          </h2>
          <p className="mt-2 text-muted-foreground">
            Dig into the rest of the platform.
          </p>
        </FadeUp>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {others.map((definition) => {
            const Icon = definition.kickerIcon;
            return (
              <a
                key={definition.slug}
                href={definition.href}
                className="group flex h-full items-start gap-4 rounded-xl border border-border bg-background p-5 transition-all hover:-translate-y-0.5 hover:border-premium-300 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-premium-100 bg-premium-50 text-premium-600">
                  <Icon className="h-5 w-5" aria-hidden />
                </div>
                <div className="min-w-0">
                  <p className="font-display text-base font-bold text-foreground">
                    {definition.navLabel}
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground line-clamp-2">
                    {definition.heroSubhead}
                  </p>
                  <span className="mt-3 inline-flex items-center text-sm font-semibold text-premium-600">
                    Learn more
                    <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const LandingSectionPage: React.FC<LandingSectionPageProps> = ({
  section,
  loggedIn,
  hasBusiness,
  routes,
  csrfToken,
  page,
  userDisplay,
  trades,
}) => {
  const definition = LANDING_SECTION_DEFINITIONS[section];
  const scrollToSection = useLandingScrollToSection();

  return (
    <div className="min-h-screen overflow-x-hidden bg-background text-foreground">
      <AppHeader
        variant="landing"
        loggedIn={loggedIn}
        hasBusiness={hasBusiness}
        routes={routes}
        csrfToken={csrfToken}
        page={page}
        userDisplay={userDisplay}
        scrollToSection={scrollToSection}
      />
      <LandingSectionHero section={section} routes={routes} />
      {definition.renderContent({ routes, scrollToSection, trades })}
      <LandingSectionExploreMore current={section} />
      {renderLandingSectionTrailingCta(definition, routes)}
      <LandingFooter
        loggedIn={loggedIn}
        hasBusiness={hasBusiness}
        routes={routes}
        scrollToSection={scrollToSection}
      />
    </div>
  );
};
